import { Stack, Typography } from '@mui/material';
import { ExportToExcelButton, ToggleFiltersButton, ToggleFiltersToolbar } from '@retail/components';
import {
  InsightReportFilterChips,
  InsightReportFilters,
  InsightReportTable,
  TotalItemsLoader,
} from '@retail/products/components';
import { useExportInsightReportToExcel, useInsightReportFilters } from '@retail/products/context';
import { useFetchSupplierItemsInsightReport } from '@retail/products/data-access';
import { useProductsTFunction } from '@retail/products/i18n';
import { SupplierItemInsight } from '@retail/products/types';
import { TagHeading } from '@shared/components';
import { useDisclosure, usePagination } from '@shared/hooks';
import { MgItemContainer, MgItemDialogContainer } from '../MgItem';
import { useState } from 'react';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { AssortmentTag } from '@retail/my-assortment/types';

export function InsightReportContainer() {
  const t = useProductsTFunction();
  const { assortmentId, isBrandContext } = useSelectedOrgUnit();

  const {
    suppliers,
    productGroupHierarchy,
    selectedFilters,
    setFilters,

    resetFilters,
  } = useInsightReportFilters();

  const { exportToExcel, isLoading: isLoadingExport } = useExportInsightReportToExcel({
    ...selectedFilters,
  });
  const [itemForProductCardDialog, setItemForProductCardDialog] = useState<number | undefined>(
    undefined
  );

  const { data: assortmentTagDtos = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
    enabled: isBrandContext,
  });
  const assortmentTags = assortmentTagDtos.map(AssortmentTag.fromDTO);

  const pagination = usePagination();

  const hasActiveFilters =
    !!selectedFilters.selectedSupplier ||
    !!selectedFilters.selectedProductGroup ||
    !!selectedFilters.searchQuery ||
    !!selectedFilters.subAssortmentIds.length;
  const {
    data: supplierItemInsightDTO,
    isLoading,
    isFetching,
  } = useFetchSupplierItemsInsightReport(
    {
      assortmentId: selectedFilters.inAssortment === 'YES' ? assortmentId : undefined,
      assortmentTagIds: selectedFilters.subAssortmentIds ?? [],
      mgParticipantAccountId: selectedFilters.selectedSupplier?.supplierNumber,
      productGroup: selectedFilters.selectedProductGroup?.groupNumber,
      articleId:
        selectedFilters.searchQuery && Number(selectedFilters.searchQuery)
          ? Number(selectedFilters.searchQuery)
          : undefined,
      hasFilterFields: selectedFilters.includedOrMissingFieldValue === 'INCLUDES',
      text: selectedFilters.searchQuery ?? undefined,
      climateFilters: selectedFilters.climateDataA1ToA3Included,
      environmentFilters: selectedFilters.environmentValuesInterface,
      page: pagination.page,
      size: pagination.pageSize,
    },
    {
      enabled: hasActiveFilters,
      suspense: false,
    }
  );

  const insightItems = supplierItemInsightDTO
    ? supplierItemInsightDTO.result?.map(SupplierItemInsight.fromDto)
    : [];

  const { isOpen: showFilters, onToggle: toggleFilters } = useDisclosure(true);

  return (
    <Stack flexGrow={1} overflow="hidden">
      <Stack>
        <Stack flexDirection="row" alignItems="center" justifyContent="space-between" px={3}>
          <TagHeading>
            <Typography variant="h4">{t('products.insightReport.title')}</Typography>
          </TagHeading>
          <ExportToExcelButton
            onClick={exportToExcel}
            loading={isLoadingExport}
            disabled={!hasActiveFilters || isLoading}
          />
        </Stack>
        <ToggleFiltersToolbar sx={{ justifyContent: 'space-between', pb: 1 }}>
          <InsightReportFilterChips
            {...selectedFilters}
            assortmentTags={assortmentTags.filter((tag) =>
              selectedFilters.subAssortmentIds.includes(tag.id.toString())
            )}
            {...setFilters}
            resetFilters={resetFilters}
          />
          <Stack flexDirection="row" alignItems="flex-end" gap={1}>
            <TotalItemsLoader totalItems={insightItems.length ?? 0} isFetching={isFetching} />
            <ToggleFiltersButton showFilters={showFilters} toggleFilters={toggleFilters} />
          </Stack>
        </ToggleFiltersToolbar>
      </Stack>
      <Stack flexDirection="row" flexGrow={1} overflow="hidden">
        <InsightReportTable
          insightItems={insightItems}
          isFetching={isLoading && isFetching}
          hasActiveFilters={hasActiveFilters}
          includeNotIncludedItemData={selectedFilters.includeNotIncludedItemData}
          pagination={pagination}
          totalItems={supplierItemInsightDTO?.totalCount}
          setItemForProductCardDialog={setItemForProductCardDialog}
        />
        <InsightReportFilters
          showFilters={showFilters}
          suppliers={suppliers}
          assortmentTags={isBrandContext ? assortmentTags : undefined}
          productGroupHierarchy={productGroupHierarchy}
          {...selectedFilters}
          {...setFilters}
        />
      </Stack>
      {itemForProductCardDialog && (
        <MgItemDialogContainer
          open
          onClose={() => setItemForProductCardDialog(undefined)}
          itemNumber={itemForProductCardDialog}
        >
          {({ mgItem }) => <MgItemContainer mgItem={mgItem} hiddenBreadcrumbs />}
        </MgItemDialogContainer>
      )}
    </Stack>
  );
}
